// ValueAnchorHint — a single line of subtle, reflective copy that
// reframes the upgrade/single-purchase decision against the cost of
// doing the same thing the slow way (hiring a designer, drawing the
// vector manually, etc.).
//
// We rotate between 3 phrasings per context so the user doesn't see
// the same line every time — keeps the message fresh and avoids the
// pattern blindness that kills static taglines.
//
// Tone is deliberately UNDERSTATED — no exclamation marks, no "act
// now", no urgency colours. The goal is reflection, not pressure.
// Visual treatment: small, italic, low-contrast text with a thin
// vertical accent bar on the left so it reads as a quote/aside.
//
// Contexts:
//   • 'ai'        — comparing an AI generation to commissioning a designer
//   • 'download'  — comparing a one-off DXF/SVG download to redrawing it
//   • 'vector'    — comparing AI vectorisation to manual tracing time
//
// Lang autodetected via useI18n.

import { useMemo } from 'react';
import { useI18n } from '../i18n';

const LINES = {
  ai: {
    pt: [
      'Um designer freelancer cobra de R$ 80 a R$ 300 por um vetor sob medida — a IA entrega em segundos.',
      'Quanto tempo você levaria para desenhar isso do zero? A IA faz em menos de 30 segundos.',
      'Contratar um designer leva dias e custa centenas de reais. Aqui são alguns créditos.',
    ],
    en: [
      'A freelance designer charges $20–$80 for a custom vector — AI delivers in seconds.',
      'How long would it take you to draw this from scratch? AI does it in under 30 seconds.',
      'Hiring a designer takes days and costs hundreds. Here, just a few credits.',
    ],
    es: [
      'Un diseñador freelance cobra de $20 a $80 por un vector a medida — la IA lo entrega en segundos.',
      '¿Cuánto tardarías en dibujarlo desde cero? La IA lo hace en menos de 30 segundos.',
      'Contratar a un diseñador toma días y cuesta cientos. Aquí, solo unos créditos.',
    ],
  },
  download: {
    pt: [
      'Redesenhar este arquivo no Inkscape levaria 1 a 3 horas. O download avulso é instantâneo.',
      'Um designer cobraria de R$ 50 a R$ 200 só para te entregar este DXF. Aqui custa centavos.',
      'O tempo que você gastaria refazendo isso à mão vale muito mais que o preço deste download.',
    ],
    en: [
      'Redrawing this file in Inkscape takes 1–3 hours. The single download is instant.',
      'A designer would charge $15–$60 just to hand you this DXF. Here it costs cents.',
      'The time you would spend redoing this manually is worth far more than this download.',
    ],
    es: [
      'Rediseñar este archivo en Inkscape lleva de 1 a 3 horas. La descarga es instantánea.',
      'Un diseñador cobraría de $15 a $60 solo por entregarte este DXF. Aquí cuesta centavos.',
      'El tiempo que tardarías rehaciéndolo a mano vale mucho más que esta descarga.',
    ],
  },
  vector: {
    pt: [
      'Vetorizar à mão no Illustrator leva 30 a 90 minutos por imagem. A IA leva 10 segundos.',
      'Estúdios cobram de R$ 40 a R$ 150 por vetorização manual. Aqui são alguns créditos.',
      'Cada vetorização manual rouba uma hora do seu dia. Quanto vale uma hora do seu trabalho?',
    ],
    en: [
      'Manual vectorisation in Illustrator takes 30–90 minutes per image. AI takes 10 seconds.',
      'Studios charge $10–$40 for manual vectorisation. Here, just a few credits.',
      'Every manual vectorisation costs you an hour of your day. What is an hour of your time worth?',
    ],
    es: [
      'Vectorizar a mano en Illustrator lleva de 30 a 90 minutos. La IA tarda 10 segundos.',
      'Los estudios cobran de $10 a $40 por vectorización manual. Aquí, solo unos créditos.',
      'Cada vectorización manual te quita una hora del día. ¿Cuánto vale una hora de tu tiempo?',
    ],
  },
};

/**
 * @param {object} props
 * @param {('ai'|'download'|'vector')} [props.context='ai']
 * @param {string} [props.className]  Extra tailwind classes for layout tweaks.
 * @param {('light'|'dark')} [props.tone='dark']  Use 'light' on dark backgrounds
 *        (downloadGate modal hero) and 'dark' on white cards (AiCreditsModal).
 */
export default function ValueAnchorHint({ context = 'ai', className = '', tone = 'dark' }) {
  const { lang } = useI18n();
  const langKey = (lang === 'pt' || lang === 'en' || lang === 'es') ? lang : 'pt';
  const variants = (LINES[context] && LINES[context][langKey]) || LINES.ai.pt;

  // Pick a rotating line — based on day-of-year so the same visitor
  // gets a different message across sessions but the same one within
  // a single browsing session (avoids the line "jumping" if the
  // component remounts mid-flow). Pure function of date → deterministic.
  const line = useMemo(() => {
    const day = Math.floor((Date.now() - Date.UTC(new Date().getUTCFullYear(), 0, 0)) / 86_400_000);
    return variants[day % variants.length];
  }, [variants]);

  const palette = tone === 'light'
    ? 'border-cyan-400/50 text-cyan-100/85'
    : 'border-cyan-500/60 text-zinc-600';

  return (
    <div
      className={`flex items-start gap-2.5 pl-3 border-l-2 ${palette} text-[12px] italic leading-snug ${className}`}
      role="note"
    >
      <span>{line}</span>
    </div>
  );
}
